import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  Globe,
  Info,
  Loader2,
  Mail,
  Pencil,
  RotateCcw,
  Search,
  Send,
  ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import { emailTemplatesApi, type EmailTemplateCatalogItem, type EmailTemplateEditorData, type EmailTemplateKey, type EmailTemplatePreview } from '@/api/emailTemplates';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { RichEmailTemplateEditor } from '../../../components/admin/mail-templates/RichEmailTemplateEditor';

interface EventOption {
  id: string;
  name: string;
}

interface MailTemplatesPageProps {
  selectedEventId?: string;
  availableEvents: EventOption[];
  isSuperAdmin: boolean;
  canManage: boolean;
}

type ScopeMode = 'GLOBAL' | 'EVENT';
type PageMode = 'list' | 'edit' | 'preview';
type FormError = { field: string; message: string };
type Notice = { type: 'success' | 'error' | 'warning'; title: string; message: string } | null;

const ACTIVE_PRIMARY_BUTTON_CLASS = 'bg-[#1e66d0] text-white hover:bg-[#1555ae] hover:text-white';

const SOURCE_LABELS: Record<EmailTemplateCatalogItem['activeSource'], { label: string; tone: string }> = {
  GLOBAL: { label: 'Usa plantilla global', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  EVENT: { label: 'Override por evento', tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  CODE: { label: 'Fallback a código por defecto', tone: 'bg-slate-50 text-slate-700 border-slate-200' },
};

const PLACEHOLDER_LABELS: Record<string, string> = {
  nombre: 'Nombre del destinatario',
  folio: 'Folio del registro o ponencia',
  evento: 'Nombre del evento',
  fecha: 'Fecha',
  anio: 'Año',
  modalidadAsistente: 'Modalidad de asistente',
  tituloPonencia: 'Título de la ponencia',
  observacionesDictamen: 'Observaciones',
  fechaLimite: 'Fecha límite',
};

const FUNCTIONAL_BLOCK_LABELS: Record<string, string> = {
  EMAIL_TPL_REG_ATTENDEE: 'Bloque protegido de pago Sysweb',
  EMAIL_TPL_SPEAKER_APPROVED: 'Bloque protegido de pago Sysweb',
  EMAIL_TPL_SPEAKER_REJECTED: 'Bloque protegido de invitación a registro como asistente',
  EMAIL_TPL_SPEAKER_OBSERVATIONS: 'Bloque protegido de corrección/subida de nueva versión',
  EMAIL_TPL_CREDENTIAL_ATTENDEE_PAID: 'Bloque protegido de credencial con QR',
  EMAIL_TPL_CREDENTIAL_ATTENDEE_FREE: 'Bloque protegido de credencial con QR',
  EMAIL_TPL_SPEAKER_PAID_UPLOAD: 'Bloque protegido de carga de material final',
  EMAIL_TPL_CREDENTIAL_SPEAKER: 'Bloque protegido de credencial con QR',
};

const PREVIEW_RENDER_INFO_KEYS: Record<EmailTemplateKey, string[]> = {
  EMAIL_TPL_REG_ATTENDEE: ['nombre', 'folio', 'evento', 'fecha', 'anio', 'modalidadAsistente'],
  EMAIL_TPL_REG_SPEAKER: ['nombre', 'folio', 'evento', 'fecha', 'anio', 'tituloPonencia'],
  EMAIL_TPL_SPEAKER_APPROVED: ['nombre', 'folio', 'evento', 'fecha', 'anio', 'tituloPonencia', 'observacionesDictamen'],
  EMAIL_TPL_SPEAKER_REJECTED: ['nombre', 'folio', 'evento', 'fecha', 'anio', 'tituloPonencia', 'observacionesDictamen'],
  EMAIL_TPL_SPEAKER_OBSERVATIONS: ['nombre', 'folio', 'evento', 'fecha', 'anio', 'tituloPonencia', 'observacionesDictamen', 'fechaLimite'],
  EMAIL_TPL_CREDENTIAL_ATTENDEE_PAID: ['nombre', 'folio', 'evento', 'fecha', 'anio', 'modalidadAsistente'],
  EMAIL_TPL_CREDENTIAL_ATTENDEE_FREE: ['nombre', 'folio', 'evento', 'fecha', 'anio', 'modalidadAsistente'],
  EMAIL_TPL_SPEAKER_PAID_UPLOAD: ['nombre', 'folio', 'evento', 'fecha', 'anio', 'tituloPonencia', 'fechaLimite'],
  EMAIL_TPL_CREDENTIAL_SPEAKER: ['nombre', 'folio', 'evento', 'fecha', 'anio', 'tituloPonencia'],
};

function getRenderInfoItems(previewData: EmailTemplatePreview) {
  const allowedKeys = PREVIEW_RENDER_INFO_KEYS[previewData.templateKey] || [];
  return allowedKeys
    .map((key) => {
      const value = previewData.placeholders[key];
      if (value == null) return null;
      const normalizedValue = String(value).trim();
      if (!normalizedValue) return null;
      return {
        key,
        label: PLACEHOLDER_LABELS[key] || key,
        value: normalizedValue,
      };
    })
    .filter(Boolean) as Array<{ key: string; label: string; value: string }>;
}

function normalizeApiErrors(error: unknown): FormError[] {
  const payload = (error as { response?: { data?: { errors?: Array<{ field?: string; message?: string }>; message?: string } } })?.response?.data;
  if (Array.isArray(payload?.errors) && payload.errors.length > 0) {
    return payload.errors.map((issue) => ({
      field: issue.field || 'body',
      message: issue.message || 'Error de validación.',
    }));
  }
  if (payload?.message) {
    return [{ field: 'body', message: payload.message }];
  }
  return [{ field: 'body', message: 'No fue posible procesar la solicitud.' }];
}

export function MailTemplatesPage({
  selectedEventId,
  availableEvents,
  isSuperAdmin,
  canManage,
}: MailTemplatesPageProps) {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<PageMode>('list');
  const [contextEventId, setContextEventId] = useState<string>(selectedEventId || '');
  const [listScope, setListScope] = useState<ScopeMode>(isSuperAdmin ? 'GLOBAL' : 'EVENT');
  const [search, setSearch] = useState('');
  const [editorData, setEditorData] = useState<EmailTemplateEditorData | null>(null);
  const [scopeMode, setScopeMode] = useState<ScopeMode>('EVENT');
  const [formValues, setFormValues] = useState({ subject: '', body: '' });
  const [formErrors, setFormErrors] = useState<FormError[]>([]);
  const [previewData, setPreviewData] = useState<EmailTemplatePreview | null>(null);
  const [notice, setNotice] = useState<Notice>(null);

  useEffect(() => {
    if (!canManage) return;
    if (selectedEventId) {
      setContextEventId(selectedEventId);
      return;
    }
    if (!isSuperAdmin && availableEvents[0]?.id) {
      setContextEventId(availableEvents[0].id);
    }
  }, [availableEvents, canManage, isSuperAdmin, selectedEventId]);

  useEffect(() => {
    if (!isSuperAdmin) {
      setListScope('EVENT');
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    if (mode !== 'edit' || !editorData || !contextEventId) return;
    if (scopeMode !== 'EVENT') return;
    loadEditorMutation.mutate({ key: editorData.key, eventId: contextEventId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextEventId]);

  const effectiveListScope: ScopeMode = isSuperAdmin ? listScope : 'EVENT';
  const canLoadTemplates =
    effectiveListScope === 'GLOBAL' ? isSuperAdmin : isSuperAdmin || Boolean(contextEventId);

  const catalogQuery = useQuery({
    queryKey: ['email-templates', effectiveListScope, effectiveListScope === 'EVENT' ? contextEventId || 'no-event' : 'global'],
    queryFn: () => emailTemplatesApi.list(effectiveListScope === 'EVENT' ? contextEventId || undefined : undefined),
    enabled: canLoadTemplates,
  });

  const filteredTemplates = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return catalogQuery.data ?? [];
    return (catalogQuery.data ?? []).filter((item) =>
      `${item.name} ${item.description}`.toLowerCase().includes(term),
    );
  }, [catalogQuery.data, search]);

  const loadEditorMutation = useMutation({
    mutationFn: ({ key, eventId }: { key: EmailTemplateKey; eventId?: string }) =>
      emailTemplatesApi.get(key, eventId),
    onSuccess: (data) => {
      setEditorData(data);
      const nextScope: ScopeMode =
        !isSuperAdmin
          ? 'EVENT'
          : effectiveListScope === 'GLOBAL'
            ? 'GLOBAL'
            : 'EVENT';
      setScopeMode(nextScope);
      setFormValues(nextScope === 'EVENT' && data.eventTemplate ? data.eventTemplate : nextScope === 'GLOBAL' && data.globalTemplate ? data.globalTemplate : data.current);
      setFormErrors([]);
      setNotice(null);
      setMode('edit');
    },
    onError: () => {
      toast.error('No fue posible cargar la plantilla.');
    },
  });

  const previewMutation = useMutation({
    mutationFn: ({ key, payload }: { key: EmailTemplateKey; payload?: { eventId?: string | null; subject?: string; body?: string } }) =>
      emailTemplatesApi.preview(key, payload),
    onSuccess: (data) => {
      setPreviewData(data);
      setNotice({
        type: 'success',
        title: 'Correo de prueba listo',
        message: 'La vista previa se generó correctamente y se enviará solo al administrador logueado.',
      });
      setMode('preview');
    },
    onError: (error) => {
      setFormErrors(normalizeApiErrors(error));
      setNotice({
        type: 'error',
        title: 'No fue posible generar la vista previa',
        message: 'Revisa el asunto, el contenido y los placeholders permitidos.',
      });
    },
  });

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!editorData) throw new Error('Plantilla no cargada.');
      return emailTemplatesApi.save(editorData.key, {
        scope: scopeMode,
        eventId: scopeMode === 'EVENT' ? contextEventId : null,
        subject: formValues.subject,
        body: formValues.body,
      });
    },
    onSuccess: async () => {
      setFormErrors([]);
      setNotice({ type: 'success', title: 'Plantilla guardada', message: 'Los cambios fueron persistidos correctamente.' });
      toast.success('Plantilla guardada correctamente.');
      await queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      if (editorData) {
        const fresh = await emailTemplatesApi.get(editorData.key, resolveEditorFetchEventId(scopeMode));
        setEditorData(fresh);
      }
    },
    onError: (error) => {
      const errors = normalizeApiErrors(error);
      setFormErrors(errors);
      setNotice({ type: 'error', title: 'Error de validación', message: errors[0]?.message || 'No fue posible guardar la plantilla.' });
    },
  });

  const restoreMutation = useMutation({
    mutationFn: ({ key, scope }: { key: EmailTemplateKey; scope: ScopeMode }) =>
      emailTemplatesApi.restore(key, {
        scope,
        eventId: scope === 'EVENT' ? contextEventId : null,
      }),
    onSuccess: async () => {
      toast.success('Plantilla restaurada a la versión por defecto.');
      setNotice({
        type: 'success',
        title: 'Plantilla restaurada',
        message: 'La personalización se eliminó y el sistema volverá a usar la herencia o el código por defecto.',
      });
      await queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      if (editorData) {
        const fresh = await emailTemplatesApi.get(editorData.key, resolveEditorFetchEventId(scopeMode));
        setEditorData(fresh);
        const refreshedValues =
          scopeMode === 'EVENT' && fresh.eventTemplate
            ? fresh.eventTemplate
            : scopeMode === 'GLOBAL' && fresh.globalTemplate
              ? fresh.globalTemplate
              : fresh.current;
        setFormValues(refreshedValues);
      }
    },
    onError: () => {
      toast.error('No fue posible restaurar la plantilla.');
    },
  });

  const sendTestMutation = useMutation({
    mutationFn: ({ key, payload }: { key: EmailTemplateKey; payload?: { eventId?: string | null; subject?: string; body?: string } }) =>
      emailTemplatesApi.sendTest(key, payload),
    onSuccess: ({ preview, deliveredTo }) => {
      setPreviewData(preview);
      setNotice({
        type: 'success',
        title: 'Correo de prueba enviado',
        message: `El correo de prueba fue enviado correctamente al administrador logueado (${deliveredTo}).`,
      });
      toast.success('Correo de prueba enviado.');
      setMode('preview');
    },
    onError: (error) => {
      const errors = normalizeApiErrors(error);
      setFormErrors(errors);
      setNotice({
        type: 'error',
        title: 'No fue posible enviar el correo de prueba',
        message: errors[0]?.message || 'Verifica el contenido antes de reintentar.',
      });
    },
  });

  const resolveDraftPayload = () => ({
    eventId: scopeMode === 'EVENT' ? contextEventId || null : null,
    subject: formValues.subject,
    body: formValues.body,
  });

  const resolveEditorFetchEventId = (scope: ScopeMode) =>
    scope === 'EVENT' ? contextEventId || undefined : undefined;

  const openEditor = (item: EmailTemplateCatalogItem) => {
    loadEditorMutation.mutate({
      key: item.key,
      eventId: effectiveListScope === 'EVENT' ? contextEventId || undefined : undefined,
    });
  };

  const openStoredPreview = (item: EmailTemplateCatalogItem) => {
    previewMutation.mutate({
      key: item.key,
      payload: { eventId: effectiveListScope === 'EVENT' ? contextEventId || null : null },
    });
  };

  const handleScopeChange = (nextScope: ScopeMode) => {
    if (!editorData) return;
    if (nextScope === 'GLOBAL' && !isSuperAdmin) return;
    setScopeMode(nextScope);
    setFormErrors([]);
    if (nextScope === 'EVENT') {
      setFormValues(editorData.eventTemplate || editorData.current);
      return;
    }
    setFormValues(editorData.globalTemplate || editorData.current);
  };

  const handleRestoreFromList = (item: EmailTemplateCatalogItem) => {
    const scope = effectiveListScope;
    restoreMutation.mutate({ key: item.key, scope });
  };

  return (
    <div className="container mx-auto space-y-6 p-6 animate-in fade-in-50 duration-300">
      {mode === 'list' ? (
        <>
          <div>
            <h1 className="text-2xl font-bold text-[#0f2744]">Plantillas de correo</h1>
            <p className="text-muted-foreground">Administra las plantillas de correo utilizadas por el sistema.</p>
          </div>

          <div className="rounded-xl border border-blue-200 bg-blue-50 px-5 py-4 text-blue-900">
            <div className="flex items-start gap-3">
              <Info className="mt-0.5 h-5 w-5 shrink-0" />
              <p className="text-sm leading-6">
                Solo puedes editar el asunto y el contenido central del correo. El header, footer, branding, QR,
                botones y enlaces críticos siguen administrados automáticamente por el sistema.
              </p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-[320px_260px_minmax(0,1fr)]">
            <div className="space-y-2">
              <label className="text-sm font-medium">Alcance activo</label>
              <div className="flex gap-2">
                {isSuperAdmin ? (
                  <>
                    <Button
                      type="button"
                      variant={effectiveListScope === 'GLOBAL' ? 'default' : 'outline'}
                      className={effectiveListScope === 'GLOBAL' ? ACTIVE_PRIMARY_BUTTON_CLASS : ''}
                      onClick={() => setListScope('GLOBAL')}
                    >
                      Plantillas globales
                    </Button>
                    <Button
                      type="button"
                      variant={effectiveListScope === 'EVENT' ? 'default' : 'outline'}
                      className={effectiveListScope === 'EVENT' ? ACTIVE_PRIMARY_BUTTON_CLASS : ''}
                      onClick={() => setListScope('EVENT')}
                    >
                      Plantillas por evento
                    </Button>
                  </>
                ) : (
                  <Badge variant="outline" className="w-fit border-slate-200 bg-white text-slate-700">
                    Evento específico
                  </Badge>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Evento</label>
              <Select
                value={contextEventId || 'ALL'}
                onValueChange={(value) => setContextEventId(value === 'ALL' ? '' : value)}
                disabled={effectiveListScope !== 'EVENT' || (!isSuperAdmin && availableEvents.length <= 1)}
              >
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder="Selecciona un evento" />
                </SelectTrigger>
                <SelectContent>
                  {isSuperAdmin ? <SelectItem value="ALL">Todos los eventos</SelectItem> : null}
                  {availableEvents.map((event) => (
                    <SelectItem key={event.id} value={event.id}>
                      {event.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Buscar plantilla</label>
              <div className="relative">
                <Search className="absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Buscar por nombre..."
                  className="pl-10"
                />
              </div>
            </div>
          </div>

          {!canLoadTemplates ? (
            <Card className="border-dashed">
              <CardContent className="py-10 text-center text-sm text-slate-500">
                Selecciona un evento para consultar las plantillas disponibles.
              </CardContent>
            </Card>
          ) : catalogQuery.isLoading ? (
            <div className="flex h-[30vh] items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-[#1e3a5f]" />
            </div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="grid grid-cols-[2.2fr_1fr_1.6fr_1.8fr] border-b bg-slate-50 px-6 py-4 text-sm font-semibold text-slate-700">
                <div>Plantilla</div>
                <div>Alcance</div>
                <div>Herencia / fallback</div>
                <div>Acciones</div>
              </div>
              {filteredTemplates.map((item) => (
                <div key={item.key} className="grid grid-cols-[2.2fr_1fr_1.6fr_1.8fr] items-center border-b px-6 py-5 last:border-b-0">
                  <div>
                    <p className="font-semibold text-[#0f2744]">{item.name}</p>
                    <p className="mt-1 text-sm text-slate-500">{item.description}</p>
                  </div>
                  <div>
                    <Badge variant="outline" className="border-slate-200 bg-white text-slate-700">
                      {effectiveListScope === 'GLOBAL' ? 'Global' : 'Evento'}
                    </Badge>
                  </div>
                  <div>
                    <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${SOURCE_LABELS[item.activeSource].tone}`}>
                      {SOURCE_LABELS[item.activeSource].label}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" onClick={() => openEditor(item)}>
                      <Pencil className="mr-2 h-4 w-4" />
                      Editar
                    </Button>
                    <Button variant="outline" onClick={() => openStoredPreview(item)}>
                      <Send className="mr-2 h-4 w-4" />
                      Enviar prueba
                    </Button>
                    <Button variant="outline" onClick={() => handleRestoreFromList(item)}>
                      <RotateCcw className="mr-2 h-4 w-4" />
                      Restaurar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-6 text-sm text-slate-500">
            <span>Mostrando {filteredTemplates.length} de {catalogQuery.data?.length ?? 0} plantillas</span>
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-slate-700">
              <span className="font-medium">Vista activa:</span>
              {effectiveListScope === 'GLOBAL'
                ? 'Plantillas globales maestras'
                : `Overrides por evento${contextEventId ? '' : ' (selecciona un evento)'}`}
            </span>
            <span className="inline-flex items-center gap-2">
              <Globe className="h-4 w-4 text-emerald-600" />
              Usa plantilla global
            </span>
            <span className="inline-flex items-center gap-2">
              <Mail className="h-4 w-4 text-amber-600" />
              Override por evento
            </span>
            <span className="inline-flex items-center gap-2">
              <RotateCcw className="h-4 w-4 text-slate-600" />
              Fallback a cÃ³digo por defecto
            </span>
          </div>
        </>
      ) : null}

      {mode === 'edit' && editorData ? (
        <>
          <div className="space-y-1">
            <button className="text-sm text-slate-500 hover:text-slate-700" onClick={() => setMode('list')}>
              ← Plantillas de correo
            </button>
            <h1 className="text-2xl font-bold text-[#0f2744]">{editorData.name}</h1>
            <p className="text-muted-foreground">{editorData.description}</p>
          </div>

          {notice ? (
            <div className={`rounded-xl border px-5 py-4 ${notice.type === 'success'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
              : notice.type === 'warning'
                ? 'border-amber-200 bg-amber-50 text-amber-900'
                : 'border-red-200 bg-red-50 text-red-900'
              }`}>
              <div className="flex items-start gap-3">
                {notice.type === 'success' ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" /> : <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />}
                <div>
                  <p className="font-semibold">{notice.title}</p>
                  <p className="text-sm mt-1">{notice.message}</p>
                </div>
              </div>
            </div>
          ) : null}

          {formErrors.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-2">
              {formErrors.map((error, index) => (
                <Card key={`${error.field}-${index}`} className="border-red-200 bg-red-50">
                  <CardContent className="flex gap-3 p-5 text-red-900">
                    <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
                    <div>
                      <p className="font-semibold">{error.field === 'subject' ? 'Error: Asunto requerido' : error.field === 'placeholders' ? 'Placeholder no reconocido' : 'HTML restringido o contenido invÃ¡lido'}</p>
                      <p className="mt-1 text-sm">{error.message}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : null}

          <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
            <Card className="shadow-sm">
              <CardContent className="space-y-6 p-6">
                <div className="space-y-3">
                  <p className="text-xl font-semibold text-[#0f2744]">Alcance de la plantilla</p>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      type="button"
                      variant={scopeMode === 'GLOBAL' ? 'default' : 'outline'}
                      className={scopeMode === 'GLOBAL' ? ACTIVE_PRIMARY_BUTTON_CLASS : ''}
                      onClick={() => handleScopeChange('GLOBAL')}
                      disabled={!isSuperAdmin}
                    >
                      Global
                    </Button>
                    <Button
                      type="button"
                      variant={scopeMode === 'EVENT' ? 'default' : 'outline'}
                      className={scopeMode === 'EVENT' ? ACTIVE_PRIMARY_BUTTON_CLASS : ''}
                      onClick={() => handleScopeChange('EVENT')}
                      disabled={!contextEventId}
                    >
                      Evento específico
                    </Button>
                    <div className="min-w-[320px] flex-1">
                      <Select value={contextEventId} onValueChange={setContextEventId} disabled={!canManage}>
                        <SelectTrigger className="bg-white">
                          <SelectValue placeholder="Selecciona un evento" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableEvents.map((event) => (
                            <SelectItem key={event.id} value={event.id}>
                              {event.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-base font-semibold text-[#0f2744]">Asunto *</label>
                  <Input
                    value={formValues.subject}
                    onChange={(event) => setFormValues((current) => ({ ...current, subject: event.target.value }))}
                    placeholder="Asunto del correo"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-base font-semibold text-[#0f2744]">Cuerpo (contenido central)</label>
                  <RichEmailTemplateEditor
                    value={formValues.body}
                    onChange={(body) => setFormValues((current) => ({ ...current, body }))}
                    placeholders={editorData.placeholders}
                  />
                  <p className="text-sm text-slate-500">
                    Solo puedes editar el contenido central del correo. El sistema mantendrá automáticamente el header,
                    footer, branding, QR, botones y enlaces críticos.
                  </p>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-6">
              <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle className="text-xl text-[#0f2744]">Placeholders permitidos</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {editorData.placeholders.map((placeholder) => (
                    <div key={placeholder} className="flex items-center justify-between gap-4 rounded-lg bg-emerald-50 px-3 py-2">
                      <code className="font-semibold text-emerald-700">{`{{${placeholder}}}`}</code>
                      <span className="text-sm text-slate-600">{PLACEHOLDER_LABELS[placeholder] || 'Placeholder permitido'}</span>
                    </div>
                  ))}
                  {FUNCTIONAL_BLOCK_LABELS[editorData.key] ? (
                    <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
                      <p className="font-semibold">Bloque funcional protegido</p>
                      <p className="mt-1">{FUNCTIONAL_BLOCK_LABELS[editorData.key]}</p>
                    </div>
                  ) : null}
                </CardContent>
              </Card>

              <Card className="border-slate-200 bg-slate-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-xl text-[#0f2744]">
                    <ShieldAlert className="h-5 w-5" />
                    Aviso de seguridad
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm text-slate-600">
                  <p>El sistema mantiene automáticamente el branding institucional, botones, QR y enlaces críticos.</p>
                  <p>No puedes modificar el wrapper completo del correo.</p>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="flex flex-wrap justify-between gap-3">
            <Button variant="outline" onClick={() => setMode('list')}>
              Cancelar
            </Button>
            <div className="flex flex-wrap gap-3">
              <Button variant="outline" onClick={() => previewMutation.mutate({ key: editorData.key, payload: resolveDraftPayload() })}>
                Vista previa
              </Button>
              <Button variant="outline" onClick={() => sendTestMutation.mutate({ key: editorData.key, payload: resolveDraftPayload() })}>
                <Send className="mr-2 h-4 w-4" />
                Enviar correo de prueba
              </Button>
              <Button variant="outline" onClick={() => restoreMutation.mutate({ key: editorData.key, scope: scopeMode })}>
                <RotateCcw className="mr-2 h-4 w-4" />
                Restaurar por defecto
              </Button>
              <Button className={ACTIVE_PRIMARY_BUTTON_CLASS} onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
                {saveMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Guardar cambios
              </Button>
            </div>
          </div>
        </>
      ) : null}

      {mode === 'preview' && previewData ? (
        <>
          <div className="space-y-1">
            <button className="text-sm text-slate-500 hover:text-slate-700" onClick={() => setMode(editorData ? 'edit' : 'list')}>
              ← Plantillas de correo
            </button>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-[#0f2744]">Vista previa del correo</h1>
              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Correo de prueba</Badge>
            </div>
          </div>

          <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
            <Card className="shadow-sm">
              <CardContent className="space-y-5 p-6">
                <div className="grid gap-2 text-sm text-slate-700">
                  <div className="grid grid-cols-[120px_1fr] gap-3"><span className="font-semibold">Para:</span><span>Administrador autenticado ({previewData.to})</span></div>
                  <div className="grid grid-cols-[120px_1fr] gap-3"><span className="font-semibold">Asunto:</span><span>{previewData.subject}</span></div>
                  <div className="grid grid-cols-[120px_1fr] gap-3"><span className="font-semibold">Evento:</span><span>{previewData.eventName}</span></div>
                </div>

                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
                  El correo de prueba se envía únicamente al administrador logueado.
                </div>

                <Tabs defaultValue="render" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="render">Vista previa (renderizado)</TabsTrigger>
                    <TabsTrigger value="source">Código fuente (solo lectura)</TabsTrigger>
                  </TabsList>
                  <TabsContent value="render" className="rounded-xl border bg-white p-4">
                    <div className="overflow-hidden rounded-lg border" dangerouslySetInnerHTML={{ __html: previewData.html }} />
                  </TabsContent>
                  <TabsContent value="source" className="rounded-xl border bg-slate-950 p-4">
                    <pre className="overflow-x-auto whitespace-pre-wrap text-xs text-slate-100">{previewData.sourceBody}</pre>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>

            <div className="space-y-6">
              <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle className="text-xl text-[#0f2744]">Información del render</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm">
                  {getRenderInfoItems(previewData).map((item) => (
                    <div key={item.key} className="space-y-1 border-b border-slate-100 pb-3 last:border-b-0 last:pb-0">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{item.label}</p>
                      <p className="whitespace-pre-wrap break-words text-sm leading-6 text-slate-700">{item.value}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Button
                className={`w-full ${ACTIVE_PRIMARY_BUTTON_CLASS}`}
                onClick={() =>
                  sendTestMutation.mutate({
                    key: previewData.templateKey,
                    payload: editorData ? resolveDraftPayload() : { eventId: contextEventId || null },
                  })
                }
                disabled={sendTestMutation.isPending}
              >
                {sendTestMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                Enviar correo de prueba
              </Button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

